package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.SessionConfig.{ParameterRestriction, StudyConfig}
import com.neurobs.ccab.{SessionConfig, SessionSearchData, StudySessionManager, StudyUpdateData, IdentityData, ValidUserData}
import com.neurobs.ccab.db.{StudyData, StudyTypeData}
import com.neurobs.errors._
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.{AdminListAdapter, AdminListController, AdminPanelController, AdminPanelDelegate}
import com.neurobs.js.dom.ElementFactory.{makeForm, makeTextInput}
import com.neurobs.js.dom.{ElementFactory, InputCheckBox, InputTextBox, SelectSelectBox, TextAreaTextBox}
import com.neurobs.js.view.{Form, Gui}
import com.neurobs.js.util.StringUtilities.dateString
import scalatags.JsDom.all._
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.RecordTableView.CommandLinkCell
import com.neurobs.util.{Guid, JsonPrinter}
import org.scalajs.dom.raw.HTMLElement
import io.circe.generic.auto._

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.{Future, Promise}

class StudyListController(accountId: Long, accountName: String, gui: Gui, dataPassword: DataPasswordCache, userData: IdentityData, addPanel: AdminPanelController => Unit, legacy: Boolean = true)(implicit server: com.neurobs.ccab.CCABServer) extends AdminListAdapter[StudyData] {

	import StudyListController._

	override def title: String = if (legacy) s"Account $accountId ($accountName) Studies" else "Studies"

	override def itemName = "Study"

	override def columnHeaders = {
		if (legacy)
			Seq(
				"Created",
				"Name",
				"Type",
				"Description",
				"Active",
				"",
				"",
				"",
				"DID"
//				,
//				""
			)
		else
			Seq(
				"Created",
				"Name",
				"Type",
				"Protocol",
				"Language",
				"Description",
				"Active",
				"",
				""
			)
	}

	override def itemRow(s: StudyData, delegate: Option[AdminPanelDelegate], controller: AdminListController[StudyData]) =
		if (legacy)
			Seq(
				dateString(s.createdAt),
				s.displayName,
				studyTypeName(s.studyTypeId),
				s.description,
				if (s.active) "Yes" else "No",
				CommandLinkCell("edit", () => createOrEdit(Some(s), controller)),
				CommandLinkCell("delete", () => delete(s, controller)),
				CommandLinkCell(text = "subjects", () =>
					for {
						passwordOpt <- dataPassword.get(accountId, gui)
						del <- delegate
					} del.newPanel(new SessionSearchController(SessionSearchData(accountId = Some(accountId), studyId = Some(s.id), dataPassword = passwordOpt), title = s"Subjects for study '${s.displayName}' (${s.id})", gui, userData, addPanel))
				),
				s.id.toString
//				,
//				CommandLinkCell(text = "tstatus", () =>
//					delegate.foreach(_.newPanel(new StudyTranscriptStatusController(s, gui)))
//				)
			)
		else {
			val studyConfig = s.config.flatMap(JsonPrinter.fromJson[StudyConfig])
			val protocolName = studyConfig.flatMap(c => config.protocols.find(_.id == c.protocol)).map(_.display_name).getOrElse("")
			val language = studyConfig.flatMap(_.parameter_restrictions).flatMap(_.find(_.identifier == languageParameterIdentifier)).flatMap(_.values.headOption).getOrElse("")
			Seq(
				dateString(s.createdAt),
				s.displayName,
				studyTypeName(s.studyTypeId),
				protocolName,
				language,
				s.description,
				if (s.active) "Yes" else "No",
				CommandLinkCell("edit", () => createOrEdit(Some(s), controller)),
				CommandLinkCell("delete", () => delete(s, controller))
			)
		}

	override val create = Some(createOrEdit(None, _))

	override def get(): Future[Either[String, Seq[StudyData]]] = {
		val studyTypesFut = server.getStudyTypes()._2
		val configFut = server.getSessionConfigData()._2
		val studiesFut = server.getStudies(accountId)._2
		val fr = for {
			types <- FutureResult(studyTypesFut)
			c <- FutureResult(configFut)
			studies <- FutureResult(studiesFut)
		} yield {
			config = c
			studyTypes = types
			studies
		}
		fr.res.map {
			case Left(e) => Left(e.description)
			case Right(data) => Right(data)
		}
	}

	override def supportClose: Boolean = legacy

	private def createOrEdit(s: Option[StudyData], c: AdminListController[StudyData]): Unit = {
		val boxWidth = 20
		val formWidth = 60

		val descriptionBox: TextAreaTextBox = textarea(style := s"width:${boxWidth}em;height:5em;").render
		descriptionBox.setValue(s.map(_.description).getOrElse(""))
		//val configBox: TextAreaTextBox = textarea(style := s"width:${boxWidth}em;height:5em;font-family:courier;").render
		//configBox.setValue(s.flatMap(_.config).getOrElse(""))
		val displayNameBox: InputTextBox = makeTextInput(boxWidth, s.map(_.displayName).getOrElse(""))
		val typeBox: SelectSelectBox = ElementFactory.makeSelectBox(boxWidth)
		val activeBox: InputCheckBox = ElementFactory.makeCheckbox(checked = s.fold(true)(_.active))
		val typeSelection = s match {
			case None => -1
			case Some(study) => studyTypes.indexWhere(_.id == study.studyTypeId)
		}
		typeBox.setOptions(studyTypes.map(_.displayName), if (typeSelection == -1) None else Some(typeSelection))

		val protocolBox: SelectSelectBox = ElementFactory.makeSelectBox(boxWidth)
		protocolBox.setOptions("None" +: config.protocols.map(_.display_name), Some(0))
		val languageBox: SelectSelectBox = ElementFactory.makeSelectBox(boxWidth)
		val languageParameterOpt = config.parameters.find(_.identifier == languageParameterIdentifier)
		languageBox.setOptions("Any" +: languageParameterOpt.map(_.values).getOrElse(Seq()), Some(0))

		for (
			data <- s;
			json <- data.config;
			studyConfig <- JsonPrinter.fromJson[StudyConfig](json)
		) {
			protocolBox.setSelected(Some(config.protocols.indexWhere(_.id == studyConfig.protocol) + 1))
			for (
				languageParameter <- languageParameterOpt;
				restrictions <- studyConfig.parameter_restrictions;
				languageRestriction <- restrictions.find(_.identifier == languageParameterIdentifier);
				value <- languageRestriction.values.headOption
			) {
				languageBox.setSelected(Some(languageParameter.values.indexWhere(_ == value) + 1))
			}
		}

		val form = makeForm(
			width = formWidth,
			title = s match {
				case Some(study) => s"Update ${study.displayName}"
				case None => "Create New Study"
			},
			description = "",
			elements = Array[(HTMLElement, HTMLElement)](
				("Name:", displayNameBox.text),
				("Type:", typeBox.select),
				("Protocol:", protocolBox.select),
				("Language:", languageBox.select),
				("Description:", descriptionBox.area),
				//("Configuration:", configBox.area),
				("Active:", activeBox.input)
			),
			useCancel = true,
			submitCaption = if (s.isDefined) "Update" else "Create"
		)

		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = {
			if (languageBox.selectedIndex.exists(_ > 0) && protocolBox.selectedIndex.forall(_ == 0))
				Left("A protocol must be selected to specify a language.")
			else if (displayNameBox.value().isEmpty || typeBox.selectedIndex.isEmpty)
				Left("Name and type are required.")
			else {
				val studyConfig: Option[StudyConfig] =
					if (protocolBox.selectedIndex.forall(_ == 0)) None
					else Some(StudyConfig(
						protocol = config.protocols(protocolBox.selectedIndex.get - 1).id,
						parameter_restrictions =
							if (languageBox.selectedIndex.forall(_ == 0)) None
							else Some(Seq(ParameterRestriction(
								identifier = languageParameterIdentifier,
								values = Seq(languageBox.value.get)
							)))
					))

				val data = StudyUpdateData(displayName = displayNameBox.value(), description = descriptionBox.value(), studyTypeId = studyTypes(typeBox.selectedIndex.get).id, active = activeBox.checked, config = studyConfig.map(JsonPrinter.jsonPrint(_)))
				Right(
					s match {
						case Some(study) =>
							CCABServer.updateStudy(study.id, data) {
								case Left(err) => callback(Some(err.message))
								case Right(res) => res.error match {
									case Some(err) => callback(Some(err))
									case None =>
										callback(None)
										c.getList()
								}
							}
						case None =>
							CCABServer.createStudy(data, accountId) {
								case Left(err) => callback(Some(err.message))
								case Right(res) => res.error match {
									case Some(err) => callback(Some(err))
									case None =>
										callback(None)
										c.getList()
								}
							}
					}
				)
			}
		}

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}

	private def delete(s: StudyData, c: AdminListController[StudyData]): Unit = {
		val form = makeForm(
			width = 30,
			title = "Delete",
			description = s"Delete Study ${s.displayName}?",
			elements = Array(),
			useCancel = true,
			submitCaption = "Delete"
		)
		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = Right(
			CCABServer.deleteStudy(s.id, accountId) {
				case Left(err) => callback(Some(err.message))
				case Right(res) => res.error match {
					case Some(err) => callback(Some(err))
					case None =>
						callback(None)
						c.getList()
				}
			}
		)
		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}

	private var config: SessionConfig = SessionConfig("", Guid.nullGuid, Seq(), Seq(), Seq(), Seq())
	private var studyTypes: Seq[StudyTypeData] = Seq()
	private def studyTypeName(studyTypeId: Long): String = {
		studyTypes.find(_.id == studyTypeId).map(_.displayName).getOrElse("Unknown")
	}
}

object StudyListController {
	def apply(gui: Gui, accountId: Long, accountName: String, dataPassword: DataPasswordCache, userData: IdentityData, addPanel: AdminPanelController => Unit, legacy: Boolean = true)(implicit server: com.neurobs.ccab.CCABServer): AdminListController[StudyData] = new AdminListController[StudyData](new StudyListController(accountId, accountName, gui, dataPassword, userData, addPanel, legacy = legacy))

	val languageParameterIdentifier = "language"
}